import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FiCreditCard, FiHome } from 'react-icons/fi';
import { MdOutlineAccountBalance } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
//zustand
import { useCartStore } from 'stores/cartStore';
import { useCompanyStore } from 'stores/companyStore';
import { useUserStore } from 'stores/userStore';
//api
import { getInfoRedsys, redsysProcessCopyDeactivateCart, updateCartDetails } from 'api/ecommerce';
import { saveChanges } from 'api/users';
import { updateCartSituationBankTransfer } from 'api/orders';
//helpers
import { goUp } from 'helpers';
import { formatCurrency } from 'utils/currencyFormatter';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import useLocalePath from 'utils/customHooks/useLocalePath';
import useCheckoutNavigation from 'utils/customHooks/useCheckoutNavigation';
//components
import TPV from './tpv';
import OrderSummary from 'components/cart/OrderSummary';
import AddressForm from 'components/addressForm';
import CheckoutLayout from 'components/checkout/CheckoutLayout';
import CheckoutStepper from 'components/checkout/CheckoutStepper';
import CheckoutHeader from 'components/checkout/CheckoutHeader';
import CheckoutNav from 'components/checkout/CheckoutNav';
import CheckoutOptionCard from 'components/checkout/CheckoutOptionCard';
import CheckoutHelpBox from 'components/checkout/CheckoutHelpBox';
//styles
import './cart.scss';

const Pay = () => {
  const { t } = useTranslation(['payment', 'shipping']);
  const location = useLocation();
  const { goToStep } = useCheckoutNavigation();
  const { id: bookstoreId, data, name, enabled_countries, bank_account } = useCompanyStore();
  const {
    billingAddress,
    benefitedBookstore,
    firstName,
    lastName,
    email,
    phoneNumber,
    id,
    pickUpLibrary,
    setBillingAddress,
    setFirstName,
    setLastName,
    isConcourseUser,
  } = useUserStore();
  const {
    isStorePickup,
    items,
    total,
    appliedCoupon,
    billingCartAddress,
    shippingCartAddress,
    contactInfo,
    totalWithDiscount,
    clear,
    setBillingCartAddress,
  } = useCartStore();
  const [processOrderBankTransferSuccess, setProcessOrderBankTransferSuccess] = useState(false);
  const [detailOrder, setDetailOrder] = useState({});
  const [validData, setValidData] = useState([false, t('completeFields', { ns: 'shipping' })]);
  // Validar que el total sea mayor que 0
  const isTotalValid = total != null && total > 0;
  // type of payment
  const [isCardPayment, setIsCardPayment] = useState(true);
  //info redsys
  const [infoRedsys, setInfoRedsys] = useState(null);
  const lastRedsysSyncKeyRef = useRef(null);
  const { libraryPath } = useAssignedLibrary();
  const localePath = useLocalePath();
  const currentPhase = useMemo(
    () => (location.state?.checkoutPhase === 'payment' ? 'payment' : 'billing'),
    [location.state]
  );

  const initialBillingValue = () => {
    if (billingAddress) {
      return {
        ...billingAddress,
        email: email || '',
        first_name: firstName || '',
        last_name: lastName || '',
        phone: billingAddress.phone || phoneNumber || '',
      };
    } else {
      return {
        address: '',
        postal_code: '',
        first_name: firstName || '',
        last_name: lastName || '',
        locality: '',
        province: '',
        country: '',
        email: email || '',
        phone: phoneNumber || '',
      };
    }
  };

  const [updateDataBilling, setUpdateDataBilling] = useState(() => initialBillingValue());
  const [finalTotal, setFinalTotal] = useState(0);

  useEffect(() => {
    goUp();
    window.document.title = t('seoTitle') + name;
    const fromRedsys = JSON.parse(localStorage.getItem('historyRedsys'));

    if (fromRedsys) {
      //! DESACTIVAR EL CARRO ACTUAL Y CREAR NUEVO A TRAVES DE EP, REDIRIGIR A CART STEP IN PROCESS
      localStorage.removeItem('historyRedsys');
      handleCopyCurrentCartRedsysProccess();
    }
    setBillingCartAddress(updateDataBilling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBillingCartAddress(updateDataBilling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataBilling]);

  useEffect(() => {
    const checkData = () => {
      if (updateDataBilling !== undefined) {
        let properties = ['first_name', 'address', 'postal_code', 'locality'];
        for (let i of properties) {
          if (updateDataBilling[`${i}`] === undefined || updateDataBilling[`${i}`] === '') {
            setValidData([false, t('completeFields', { ns: 'shipping' })]);
            return '';
          }
        }
        setValidData([
          true,
          updateDataBilling.address + ', ' + updateDataBilling.postal_code + ', ' + updateDataBilling.locality,
        ]);
      }
    };
    checkData();
  }, [t, updateDataBilling]);

  const handleChangeSave = async (values) => {
    let payload = {
      first_name: values?.first_name,
      last_name: values?.last_name,
      email: values?.email,
      phone: values?.phone,
      address: values?.address,
      country: values?.country,
      province: values?.province?.label || values?.province,
      locality: values?.locality,
      postal_code: values?.postal_code,
    };
    try {
      // Actualizar tanto la dirección de facturación como el nombre y apellido del usuario
      const changes = await saveChanges({ 
        billing_address: payload,
        first_name: values?.first_name,
        last_name: values?.last_name
      }, id);
      if (changes.status === 200) {
        payload.province = values?.province || {};
        setBillingAddress(payload);
        // Actualizar también el nombre y apellido en el store del usuario
        if (values?.first_name) {
          setFirstName(values.first_name);
        }
        if (values?.last_name) {
          setLastName(values.last_name);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopyCurrentCartRedsysProccess = async () => {
    //! CAMBIA ESTADO DEL CARRO ACTUAL A DESACTIVADO Y CREA UNA COPIA PARA CONTROL RETORNO SIN PAGO DE REDSYS
    try {
      await redsysProcessCopyDeactivateCart({ items });
    } catch (error) {
      console.error(`Error ep - redsysProcessCopyDeactivateCart: ${error}`);
    }
  };

  const handleUpdateCartDetails = async () => {
    // Usar updateDataBilling directamente si tiene datos, sino usar billingCartAddress
    const billingToUse = (updateDataBilling?.first_name && updateDataBilling?.address) 
      ? updateDataBilling 
      : billingCartAddress;
    
    const payload = {
      items: items,
      empresa: bookstoreId,
      store_pickup: isStorePickup,
      billing_address: billingToUse,
      shipping_address: shippingCartAddress,
      bookstore_to_pickup: pickUpLibrary,
      benefited_company: benefitedBookstore?.value,
      contact_info: contactInfo,
    };
    try {
      await updateCartDetails(payload);
    } catch (error) {
      console.error('Error updating cart details', error);
    }
  };

  const handleCartBankTransfer = async () => {
    // Validar que el total sea mayor que 0
    if (!isTotalValid) {
      return;
    }
    try {
      await handleUpdateCartDetails();
      const { status, data } = await updateCartSituationBankTransfer();
      if (status === 200) {
        const finalTotal = totalWithDiscount;
        setDetailOrder(data);
        setProcessOrderBankTransferSuccess(true);
        clear();
        setFinalTotal(finalTotal);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const callGetInfoRedsys = async () => {
    let payload = { applied_coupon: appliedCoupon };
    try {
      const { status, data } = await getInfoRedsys(payload);
      if (status === 200) {
        setInfoRedsys(data);
      }
    } catch (error) {
      console.error('Error getInfoRedsys', error);
    }
  };

  const redsysSyncKey = useMemo(
    () =>
      JSON.stringify({
        currentPhase,
        isConcourseUser,
        isCardPayment,
        appliedCoupon: appliedCoupon || '',
        isStorePickup,
        firstName: updateDataBilling?.first_name || '',
        address: updateDataBilling?.address || '',
        postalCode: updateDataBilling?.postal_code || '',
        locality: updateDataBilling?.locality || '',
      }),
    [appliedCoupon, currentPhase, isCardPayment, isConcourseUser, isStorePickup, updateDataBilling]
  );

  useEffect(() => {
    const updateCartAndGetRedsys = async () => {
      // Solo sincronizar al entrar en la fase de pago con tarjeta
      if (currentPhase !== 'payment' || isConcourseUser || !isCardPayment || !validData[0]) {
        return;
      }

      // Evita llamadas duplicadas con la misma combinación de datos
      if (lastRedsysSyncKeyRef.current === redsysSyncKey) {
        return;
      }
      lastRedsysSyncKeyRef.current = redsysSyncKey;

      await handleUpdateCartDetails();
      await callGetInfoRedsys();
    };

    updateCartAndGetRedsys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhase, isConcourseUser, isCardPayment, redsysSyncKey, validData]);

  const goToPaymentPhase = useCallback(() => {
    if (!validData[0] || !isTotalValid) {
      return;
    }
    goToStep(3);
  }, [goToStep, isTotalValid, validData]);

  const renderPaymentSelection = () => (
    <div
      className={`checkout-card checkout-card--centered ${
        isConcourseUser ? 'checkout-payment-options--contest' : 'checkout-payment-options'
      }`}
    >
      <p className="checkout-card__eyebrow">{t('requiredStep', { ns: 'cart' })}</p>
      <h2 className="checkout-card__title">{t('selectPaymentTitle')}</h2>
      <p className="checkout-card__subtitle">{t('selectPaymentSubtitle')}</p>
      {isConcourseUser ? (
        <div className="checkout-options-grid">
          <CheckoutOptionCard
            icon={<FiHome />}
            title={t('contestPayment')}
            description={t('contestPayment')}
            accent="blue"
            isActive
            onSelect={() => null}
          />
        </div>
      ) : (
        <div className="checkout-options-grid">
          <CheckoutOptionCard
            icon={<FiCreditCard />}
            title={t('cardPaymentOptionTitle')}
            description={t('cardPaymentOptionDescription')}
            accent="orange"
            isActive={isCardPayment}
            onSelect={() => setIsCardPayment(true)}
          />
          <CheckoutOptionCard
            icon={<MdOutlineAccountBalance />}
            title={t('bankTransfer')}
            description={t('bankTransferOptionDescription')}
            accent="blue"
            isActive={!isCardPayment}
            onSelect={() => setIsCardPayment(false)}
          />
        </div>
      )}
    </div>
  );

  return (
    <CheckoutLayout sidebar={total > 0 && <OrderSummary activeStep={currentPhase === 'payment' ? 3 : 2} />}>
      {!processOrderBankTransferSuccess ? (
        <>
          <CheckoutStepper activeStep={currentPhase === 'payment' ? 3 : 2} />
          <CheckoutHeader
            step={currentPhase === 'payment' ? 4 : 3}
            title={currentPhase === 'payment' ? t('paymentMethod') : t('billingDataTitle')}
          />

          {currentPhase === 'billing' ? (
            <div className="checkout-card">
              <h2 className="checkout-card__title">{t('addressAndBillingData')}</h2>
              <p className="checkout-card__subtitle">{t('billingAddressDescription')}</p>
              <AddressForm
                data={updateDataBilling}
                onChange={(newData) => {
                  setUpdateDataBilling(newData);
                  setBillingCartAddress(newData);
                }}
                onSave={() => handleChangeSave(updateDataBilling)}
                countries={enabled_countries}
                hideCancelButton
                isBillingAddressDetails
              />
            </div>
          ) : (
            renderPaymentSelection()
          )}

          <CheckoutNav
            backButton={
              <Button
                className="checkout-btn checkout-btn--ghost"
                as={Link}
                to={currentPhase === 'payment' ? libraryPath('pago') : libraryPath('envio')}
                state={currentPhase === 'payment' ? { checkoutPhase: 'billing' } : undefined}
              >
                ← {t('goBack')}
              </Button>
            }
            nextButton={
              currentPhase === 'billing' ? (
                !validData[0] || !isTotalValid ? (
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="button-tooltip-next">
                        {!isTotalValid ? t('totalMustBeGreaterThanZero') : t('completeFields', { ns: 'shipping' })}
                      </Tooltip>
                    }
                  >
                    <span className="d-inline-block">
                      <Button className="checkout-btn checkout-btn--primary disabled" disabled>
                        {t('following', { ns: 'cart' })} →
                      </Button>
                    </span>
                  </OverlayTrigger>
                ) : (
                  <Button className="checkout-btn checkout-btn--primary" onClick={goToPaymentPhase}>
                    {t('following', { ns: 'cart' })} →
                  </Button>
                )
              ) : isConcourseUser ? (
                <TPV redsys={infoRedsys} infoDataDisabled={!validData[0] || !isTotalValid} />
              ) : isCardPayment ? (
                <TPV redsys={infoRedsys} infoDataDisabled={!validData[0] || !isTotalValid} />
              ) : !validData[0] || !isTotalValid ? (
                <OverlayTrigger
                  overlay={
                    <Tooltip id="button-tooltip2">
                      {!isTotalValid ? t('totalMustBeGreaterThanZero') : t('completeFields', { ns: 'shipping' })}
                    </Tooltip>
                  }
                >
                  <span className="d-inline-block">
                    <Button className="checkout-btn checkout-btn--primary disabled" disabled>
                      {t('finishPurchase')} →
                    </Button>
                  </span>
                </OverlayTrigger>
              ) : (
                <Button
                  onClick={() => {
                    if (!isCardPayment && isTotalValid) {
                      handleCartBankTransfer(true);
                    }
                  }}
                  className="checkout-btn checkout-btn--primary"
                  disabled={!isTotalValid}
                >
                  {t('finishPurchase')} →
                </Button>
              )
            }
          />
          <CheckoutHelpBox />
        </>
      ) : (
        <div className="checkout-card text-center">
          <p>{t('orderSuccess')}</p>
          <p>{t('receiveMailOrder')}</p>
          <Row className="justify-content-center">
            <Col md={10}>
              <Table bordered hover variant="dark">
                <tbody>
                  <tr>
                    <td colSpan="2">
                      <h3>{t('makeTransfer')}</h3>{' '}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('bankAccount')}</td>
                    <td>{bank_account}</td>
                  </tr>
                  <tr>
                    <td>{t('concept')}</td>
                    <td>{detailOrder?.order} </td>
                  </tr>
                  <tr>
                    <td>{t('totalTransfer')}</td>
                    <td>{formatCurrency(finalTotal)}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={8} className="container-messge-transfer">
              <p>
                {t('messageTransfer')}
                <b>{data?.cif}</b> {t('messageNumberOrder')}
                <b>{detailOrder?.order}</b>.
              </p>
              <p>{t('bankTransferDelayOrder')}</p>
              <p>{t('thanks')}</p>
              <Button className="checkout-btn checkout-btn--primary mt-3" as={Link} to={localePath('/')}>
                {t('backHome')}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </CheckoutLayout>
  );
};

export default Pay;
